import { useDecision } from '@optimizely/react-sdk'
import { useEffect, useState } from 'react'

import { type ListingFragment, type SearchCategory } from '@/generated'
import { FEATURE_FLAG } from '@/lib/optimizely'

import { getCrossPromoConfig } from '../components/srp/search-list/domain'

type UseSearchListCrossPromoConfigReturnType = {
  listingsWithCrossPromotion?: string[]
}
export const CROSS_PROMO_LISTINGS_PER_PAGE = 1

/**
 * `useSearchListCrossPromoConfig` is a custom hook that returns a list of listing ids that applies to a cross promo.
 *
 * @param {ListingFragment[]} listings - An array of ListingFragment objects.
 *
 * @param {SearchCategory} currentCategory - An object representing the current category
 *
 * @returns {number[]} listingsWithCrossPromotion - A list of listing ids that applies to a cross promo.
 *
 * @example
 * const { listingsWithCrossPromotion } = useSearchListCrossPromoConfig(listings, category);
 */
export const useSearchListCrossPromoConfig = (
  listings: ListingFragment[],
  currentCategory: SearchCategory
): UseSearchListCrossPromoConfigReturnType => {
  const [decision] = useDecision(FEATURE_FLAG.SRP_CROSS_PROMO)

  const [isSrpCrossPromoToggleEnabled, setIsSrpCrossPromoToggleEnabled] = useState(false)
  useEffect(() => {
    /**
     * decision.enabled is giving us an hydration error
     * On SSR it returns false and then true on client and impacts the UI
     * TODO: This is a deeper issue on how we use Optimizely and we will be investigating what is the best solution for it.
     */
    setIsSrpCrossPromoToggleEnabled(decision.enabled)
  }, [decision.enabled])

  if (!isSrpCrossPromoToggleEnabled) return { listingsWithCrossPromotion: [] }
  const listingsWithCrossPromotion = getCrossPromoConfig(
    listings,
    currentCategory,
    CROSS_PROMO_LISTINGS_PER_PAGE
  )

  return { listingsWithCrossPromotion }
}
